/**
 * §4.2: what a korps says about its ploegen - the list, the two-step wizard behind it, and the
 * window's own status.
 */
export const inschrijving = {
  /** Only drawn where a seizoen holds more than one window for this doelgroep (ADR-0031). */
  kiezer: {
    label: "Inschrijfperiode",
    gesloten: "{{naam}} (gesloten)",
  },

  venster: {
    geenPeriode: "Er staat nog geen inschrijfperiode open.",
    open: "Open tot {{moment}}.",
    opent: "De inschrijving opent op {{moment}}.",
    gesloten: "De inschrijving is gesloten sinds {{moment}}.",
    /** The periode's name in front of its window - "Ploegen 2027 · Open tot 1 maart 2027 17:00." */
    metPeriode: "{{periode}} · {{venster}}",
  },

  lijst: {
    inschrijven: "Ploeg inschrijven",
    kiesPloeg:
      "Kies een ploeg om de klassen en voorkeursdatums te wijzigen of de inschrijving in te trekken.",
    label: "Ploegen",
    ploeg: "Ploeg",
    voorkeursdatums: "Voorkeursdatums",
    geenKlasse: "Nog geen klasse",
    gepromoveerd: "Gepromoveerd naar {{bestemming}}",
    geenVoorkeuren: "Nog niets ingevuld",
    leeg: "Dit korps heeft nog geen ploegen ingeschreven voor deze inschrijfperiode.",
    /** One voorkeur in the cell's single line. `rang` is already the ordinal digit. */
    voorkeurKort: "{{rang}}e {{datum}}",
    geenVoorkeurKort: "Liever niet {{datum}}",
    onbekendeDag: "?",
  },

  pagina: {
    terug: "Terug naar mijn ploegen",
    nieuweTitel: "Ploeg inschrijven",
    korpsMetPeriode: "{{periode}} · {{venster}}",
    geenKorps: {
      title: "Geen korps gekozen",
      description: "Kies eerst het korps waarvoor je inschrijft.",
    },
    kwijt: {
      title: "Deze ploeg bestaat niet meer",
      description:
        "De ploeg is ingetrokken, of de link hoort bij een ander korps.",
    },
    intrekken: "Ploeg intrekken",
    intrekkenTitle: "{{naam}} intrekken?",
    intrekkenDescription:
      "De klassen en voorkeursdatums van deze ploeg verdwijnen ook. Je kunt het intrekken niet ongedaan maken. Daarna kun je wel een nieuwe ploeg inschrijven.",
    intrekkenConfirm: "{{naam}} intrekken",
    inschrijvenLabel: "Inschrijven",
  },

  wizard: {
    stap: "Stap {{stap}} van 2",
    naam: {
      label: "Naam van de ploeg",
      description:
        "Laat je de naam leeg? Dan heet de ploeg <strong>{{standaard}}</strong>. Is die naam in een van de gekozen klassen al bezet? Dan zetten we er een nummer achter. Je kunt de naam later nog wijzigen.",
    },
    klassen: {
      legend: "Klassen",
      description:
        "Kies de klassen waarin deze ploeg meedoet. Heeft je korps meer dan één ploeg? Schrijf dan elke ploeg apart in, met een eigen naam.",
      verplicht: "Kies eerst een klasse.",
    },
    voorkeuren: {
      legend: "Voorkeursdatums",
      /** The klasse leads: it tells two otherwise identical tables apart, also in a screen reader. */
      legendKlasse: "Voorkeursdatums {{klasse}}",
      kolomDag: "Wedstrijddag",
      kolomReistijd: "Reistijd",
      kolomVoorkeur: "Voorkeur",
      kolomGeenVoorkeur: "Liever niet",
      badgeVoorkeur: "{{rang}}e voorkeur",
      badgeGeenVoorkeur: "Liever niet",
      // The klasse is in both labels: the same date appears in every table on this page, so
      // without it a screen reader announces the same checkbox name two or three times.
      vinkVoorkeur: "Voorkeur voor {{datum}} in {{klasse}}",
      vinkGeenVoorkeur: "Liever niet op {{datum}} in {{klasse}}",
      geenDagen:
        "Dit seizoen is er geen wedstrijddag voor {{klasse}}. Kies in stap 1 een andere klasse.",
      /**
       * Built from three pieces because a beheerder may turn either half off, and the counts are
       * clamped to the dagen actually shown.
       */
      description:
        "{{regels}} Kruis je bij een dag niets aan? Dan kan je ploeg ook op die dag spelen. Je ziet alleen de wedstrijddagen voor deze klasse. De reistijd is een schatting van de rijtijd met de auto vanaf je korps.",
      regelVoorkeur_one: "Kruis maximaal één dag aan als voorkeur.",
      regelVoorkeur_other:
        "Kruis maximaal {{count}} dagen aan als voorkeur. De eerste dag die je aankruist, is je eerste voorkeur.",
      regelGeenVoorkeur_one:
        "Kruis maximaal één dag aan bij Liever niet: de dag die voor je korps echt niet uitkomt.",
      regelGeenVoorkeur_other:
        "Kruis maximaal {{count}} dagen aan bij Liever niet: de dagen die voor je korps echt niet uitkomen.",
      /** Which klassen a dag hosts, under its date. Information, never a rule. */
      geenKlassenOpDag: "geen klassen van deze inschrijving",
    },
    /**
     * The samenvoorkeur (ADR-0052). The description stays conditional so a korps with one ploeg
     * reads a condition rather than a claim, and it promises nothing: the planner weighs this
     * below the voorkeursdatums.
     */
    samen: {
      label: "Samen met andere ploegen van jouw korps",
      description:
        "Zet dit aan als deze ploeg liever op dezelfde wedstrijddag speelt als de andere ploegen van je korps. JBNL houdt daar rekening mee, maar geeft geen garantie.",
    },
    /** The shared VR vehicle (ADR-0077). Like the samenvoorkeur, a preference the planner cannot promise. */
    vr: {
      label: "Komt met een gedeeld VR-voertuig",
      description:
        "Zet dit aan als deze ploeg een voertuig van de veiligheidsregio deelt met andere ploegen, ook van andere korpsen. JBNL plant dan tijd tussen de wedstrijden van deze ploegen, maar geeft geen garantie.",
    },
  },

  errors: {
    periodesOphalen:
      "We konden de inschrijfperiodes niet ophalen. Laad de pagina opnieuw.",
    ophalen: "We konden de inschrijving niet ophalen. Laad de pagina opnieuw.",
    inschrijven: "We konden de ploeg niet inschrijven. Probeer het opnieuw.",
    opslaan: "We konden de inschrijving niet opslaan. Probeer het opnieuw.",
    intrekken: "We konden de ploeg niet intrekken. Probeer het opnieuw.",
  },
} as const;
